import { Combobox, createListCollection, Text } from '@chakra-ui/react'
import { Check, ChevronsUpDown } from 'lucide-react'
import React, { useEffect, useMemo, useRef, useState } from 'react'

import type { PhoneNumbersTranslateFn } from '../types/i18n.types'
import { text } from '../types/i18n.types'
import type { SelectOption } from '../types/phoneNumber.types'

const NONE_VALUE = '__none__'

interface ExtensionOption {
  id: string
  label: string
  searchText: string
}

interface ExtensionComboboxProps {
  extensions: SelectOption[]
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  t?: PhoneNumbersTranslateFn
}

/**
 * Searchable combobox for picking an extension on a phone number. Replaces
 * a native `<select>` of every line on the PBX, which got unusable on bigger
 * tenants. Filtering is client-side because the page already fetches the
 * full PBX-scoped list up front (see `messagingApi.getExtensions` patterns).
 *
 * Two behaviors worth noting:
 *
 * 1. The "None" option is always visible regardless of the search term so
 *    users can always clear the selection without backing out the typed
 *    text first.
 *
 * 2. The currently-assigned extension is expected to already be present in
 *    the `extensions` prop (the modal merges it in defensively via
 *    `mergedExtensions` to guard against API list pagination/filter
 *    mismatches — see PR #1488). This component just renders what it's
 *    given; it does not synthesize fallbacks of its own.
 */
export const ExtensionCombobox: React.FC<ExtensionComboboxProps> = ({
  extensions,
  value,
  onChange,
  disabled,
  t,
}) => {
  const noneLabel = text(t, 'phoneNumbers.edit.noExtension', 'None')
  const searchPlaceholder = text(
    t,
    'phoneNumbers.edit.extensionSearchPlaceholder',
    'Search extensions...',
  )
  const searchAriaLabel = text(
    t,
    'phoneNumbers.edit.extensionSearchAriaLabel',
    'Search extensions by name or number',
  )
  const emptyLabel = text(
    t,
    'phoneNumbers.edit.noExtensionMatches',
    'No extension matches that search.',
  )

  const extensionOptions = useMemo<ExtensionOption[]>(
    () =>
      extensions.map((ext) => {
        // Mirror the format used by the legacy native-select:
        //   "PQA Tester - 1006"
        // but avoid "1006 - 1006" for synthesized fallback rows where we
        // had to set name = number because the real name wasn't available.
        const label =
          ext.number && ext.name !== ext.number
            ? `${ext.name} - ${ext.number}`
            : ext.name
        // The picker's value is the extension UUID (what the API expects on
        // save), falling back to the numeric id when uuid is absent.
        const id = ext.uuid ?? String(ext.id)
        const searchText = `${ext.name ?? ''} ${ext.number ?? ''}`.toLowerCase()
        return { id, label, searchText }
      }),
    [extensions],
  )

  const selectedOption = useMemo(() => {
    if (!value) return null
    return extensionOptions.find((option) => option.id === value) ?? null
  }, [extensionOptions, value])

  const [inputValue, setInputValue] = useState(selectedOption?.label ?? '')

  // Tracks whether the popover is closing because the user just picked
  // an option (versus blurring with a stale search term in the field).
  // The selection path already sets `inputValue` to the new option's
  // label; the close-blur reset below would otherwise undo that because
  // `selectedOption` is computed from the `value` prop and the parent
  // hasn't propagated the new value back yet by the time `onOpenChange`
  // fires.
  const justSelectedRef = useRef(false)

  // Keep the rendered label in sync with the selection when the value
  // changes from outside (e.g. modal reopen) or when the extensions list
  // loads late and can finally resolve the selected extension's name.
  useEffect(() => {
    setInputValue(selectedOption?.label ?? '')
  }, [selectedOption])

  const filteredExtensions = useMemo(() => {
    const trimmed = inputValue.trim().toLowerCase()
    const matchesSelected =
      !!selectedOption && selectedOption.label.trim().toLowerCase() === trimmed

    if (!trimmed || matchesSelected) return extensionOptions

    return extensionOptions.filter((option) =>
      option.searchText.includes(trimmed),
    )
  }, [extensionOptions, inputValue, selectedOption])

  const visibleOptions = useMemo<ExtensionOption[]>(
    () => [
      { id: NONE_VALUE, label: noneLabel, searchText: '' },
      ...filteredExtensions,
    ],
    [filteredExtensions, noneLabel],
  )

  const collection = useMemo(
    () =>
      createListCollection<ExtensionOption>({
        items: visibleOptions,
        itemToValue: (item) => item.id,
        itemToString: (item) => item.label,
      }),
    [visibleOptions],
  )

  const comboboxValue = value ? [value] : [NONE_VALUE]

  const hasSearchTerm = inputValue.trim().length > 0
  const isSearchMatchingSelected =
    !!selectedOption &&
    selectedOption.label.trim().toLowerCase() ===
      inputValue.trim().toLowerCase()
  const showNoMatchesHint =
    hasSearchTerm &&
    !isSearchMatchingSelected &&
    filteredExtensions.length === 0

  return (
    <Combobox.Root
      collection={collection}
      value={comboboxValue}
      inputValue={inputValue}
      onValueChange={(details) => {
        const next = details.value[0] ?? ''
        justSelectedRef.current = true
        if (!next || next === NONE_VALUE) {
          onChange('')
          setInputValue('')
          return
        }
        onChange(next)
        const match = extensionOptions.find((option) => option.id === next)
        setInputValue(match?.label ?? '')
      }}
      onInputValueChange={(details) => {
        setInputValue(details.inputValue)
      }}
      onOpenChange={(details) => {
        if (details.open) return
        if (justSelectedRef.current) {
          // Selection path already set `inputValue` to the new label —
          // don't clobber it with the stale `selectedOption` from this
          // render's closure.
          justSelectedRef.current = false
          return
        }
        // Popover closed without a selection (escape, click-away, blur).
        // Wipe any stray search term so we don't leave a partial query
        // showing the wrong label in the field.
        setInputValue(selectedOption?.label ?? '')
      }}
      openOnClick
      disabled={disabled}
      width="100%"
      size="md"
    >
      <Combobox.Control>
        <Combobox.Input
          placeholder={searchPlaceholder}
          aria-label={searchAriaLabel}
          // Select the existing label on focus so users can immediately
          // type a search term to replace the assigned extension's
          // name. Deferred to the next animation frame because Ark UI's
          // own focus handler runs after this one and resets the cursor
          // to the end of the value — calling `select()` synchronously
          // here gets undone on the same tick.
          onFocus={(e) => {
            const input = e.currentTarget
            requestAnimationFrame(() => input.select())
          }}
        />
        <Combobox.IndicatorGroup>
          <Combobox.Trigger>
            <ChevronsUpDown />
          </Combobox.Trigger>
        </Combobox.IndicatorGroup>
      </Combobox.Control>
      <Combobox.Positioner>
        <Combobox.Content maxH="280px" overflowY="auto">
          {showNoMatchesHint && (
            <Text
              px={3}
              py={2}
              fontSize="sm"
              color="fg.muted"
              role="status"
              aria-live="polite"
            >
              {emptyLabel}
            </Text>
          )}
          {visibleOptions.map((option) => (
            <Combobox.Item key={option.id} item={option}>
              <Combobox.ItemText>{option.label}</Combobox.ItemText>
              <Combobox.ItemIndicator>
                <Check />
              </Combobox.ItemIndicator>
            </Combobox.Item>
          ))}
        </Combobox.Content>
      </Combobox.Positioner>
    </Combobox.Root>
  )
}
